import { Control, FieldValues, Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select';
// The portion select needs an onChange hook to pre-select the part-of-day in the
// same tick (the base SingleSelect doesn't expose one); this variant does.
import { SingleSelect as SingleSelectWithChange } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { formatDisplayDate, formatShortWeekday } from '@/utils/date-formatters';

import { DURATION_LABEL_KEY } from '../../constants/time-off-duration-labels';
import { DAY_PORTION_ORDER, getDayPartOptions } from '../../constants/time-off-portion-options';
import { DayPortion } from '../../types/time-off-request';
import { TimeOffHourlyInput } from './time-off-hourly-input';

interface TimeOffRequestDurationRowProps<T extends FieldValues> {
  control: Control<T>;
  /** ISO date of the day (for the read-only date label). */
  date: string;
  /** Field name of the day's portion select. */
  portionName: Path<T>;
  /** Field name of the day's 1-based day-part number select (HALF/QUARTER). */
  dayPartNumberName: Path<T>;
  /** Field name of the day's minutes input (HOURLY). */
  minutesName: Path<T>;
  /** When false this is a non-working day — shown disabled with a placeholder. */
  isWorkingDay?: boolean;
  /** Read/approve modes disable every control and offer a single fixed option. */
  disabled: boolean;
  /**
   * Create mode: which portions the type's policy permits. FULL is always
   * allowed. When only FULL is allowed the select is locked (nothing to choose).
   * Omitted (read/approve) → no filtering.
   */
  allowedPortions?: DayPortion[];
}

/**
 * One row of the "Duration and part of day" section: a read-only date label
 * followed by the portion select and the dependent second field.
 *
 * The second field cascades off the selected portion:
 *  - FULL    → no second field
 *  - HALF    → 1st/2nd half select (dayPartNumber 1-2)
 *  - QUARTER → 1st-4th quarter select (dayPartNumber 1-4)
 *  - HOURLY  → single free-form duration input (minutes)
 *
 * In read/approve mode all controls are disabled and the portion select offers
 * only the request's own value. Non-working days render fully disabled.
 */
export const TimeOffRequestDurationRow = <T extends FieldValues>({
  control,
  date,
  portionName,
  dayPartNumberName,
  minutesName,
  isWorkingDay = true,
  disabled,
  allowedPortions,
}: TimeOffRequestDurationRowProps<T>) => {
  const { t } = useTranslation('authenticated.timeOff');
  const { setValue } = useFormContext<T>();

  const portion = useWatch({ control, name: portionName }) as DayPortion | undefined;

  const dateLabel = `${formatDisplayDate(date)} (${formatShortWeekday(date)})`;

  // Pre-select 1st half / 1st quarter when switching to HALF/QUARTER, and clear
  // the part for FULL/HOURLY. Done synchronously in the portion select's onChange
  // (same tick as the portion update) so the day never flashes a validation error
  // for a momentarily-empty part.
  const handlePortionChange = (value: string) => {
    const nextPart = value === DayPortion.Half || value === DayPortion.Quarter ? '1' : '';
    setValue(dayPartNumberName, nextPart as never, { shouldValidate: true, shouldDirty: true });
  };

  // Editable rows list the policy-allowed portions (FULL always allowed); disabled
  // rows expose only the current value.
  const selectablePortions = allowedPortions
    ? DAY_PORTION_ORDER.filter(
        value => value === DayPortion.Full || allowedPortions.includes(value)
      )
    : DAY_PORTION_ORDER;
  // Only FULL available → lock the select (nothing else to pick).
  const isPortionLocked = !disabled && selectablePortions.length <= 1;

  const portionOptions = disabled
    ? portion
      ? [{ value: portion, label: t(DURATION_LABEL_KEY[portion]) }]
      : []
    : selectablePortions.map(value => ({ value, label: t(DURATION_LABEL_KEY[value]) }));

  const dayPartOptions = portion
    ? getDayPartOptions(portion).map(option => ({
        value: String(option.value),
        label: t(option.labelKey),
      }))
    : [];

  const isFull = portion === DayPortion.Full;
  const isHourly = portion === DayPortion.Hourly;

  if (!isWorkingDay) {
    return (
      <div className="grid grid-cols-3 gap-2 text-xs sm:text-sm">
        <div className="flex h-10 items-center rounded-md bg-muted px-2 font-medium sm:px-3">
          <span className="truncate">{dateLabel}</span>
        </div>
        <div className="flex h-10 items-center truncate rounded-md bg-muted px-2 text-muted-foreground sm:px-3">
          {t('details.sections.nonWorkingDay')}
        </div>
        <div className="flex h-10 items-center truncate rounded-md bg-muted px-2 text-muted-foreground sm:px-3">
          {t('details.sections.nonWorkingDay')}
        </div>
      </div>
    );
  }

  return (
    // Always 3 columns (even on mobile — fonts shrink to fit). `items-start`
    // keeps every cell top-aligned so the hourly input's inline error message
    // (rendered below its field) doesn't shift the sibling selects.
    <div className="grid grid-cols-3 items-start gap-2 text-xs sm:text-sm">
      <div className="flex h-10 items-center rounded-md bg-muted px-2 font-medium sm:px-3">
        <span className="truncate">{dateLabel}</span>
      </div>
      <SingleSelectWithChange
        control={control}
        name={portionName}
        options={portionOptions}
        disabled={disabled || isPortionLocked}
        searchable={false}
        className="text-xs sm:text-sm"
        onChange={handlePortionChange}
      />

      {isFull ? (
        <div />
      ) : isHourly ? (
        <TimeOffHourlyInput control={control} name={minutesName} disabled={disabled} />
      ) : (
        <SingleSelect
          control={control}
          name={dayPartNumberName}
          options={dayPartOptions}
          disabled={disabled}
          searchable={false}
          placeholder={t('details.selectPartOfDay')}
          className="text-xs sm:text-sm"
        />
      )}
    </div>
  );
};
